import { useState } from 'react';
import { NICKNAME_MAX, NicknameSchema } from '@minigame-race/contracts';
import { PrimaryButton } from '../components/Chrome.js';
import { color } from '../theme.js';

/**
 * Shown once, over the whole app, right after a signed-up user's first sign-in:
 * pick a nickname before playing. Dismisses itself when `onSave` resolves
 * successfully (the parent clears `needsNickname`, unmounting this).
 */
export function NicknameOverlay({
  suggestion,
  onSave,
}: {
  /** A pre-filled starting point (e.g. the email/Google name), if any. */
  suggestion?: string;
  /** Persist the nickname. Resolves to an error message, or null on success. */
  onSave: (nickname: string) => Promise<string | null>;
}) {
  const [value, setValue] = useState(suggestion ?? '');
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const parsed = NicknameSchema.safeParse(value);
  const localError = value.length === 0 ? null : parsed.success ? null : parsed.error.issues[0]?.message ?? null;
  const canSave = parsed.success && !busy;

  const submit = async (): Promise<void> => {
    if (!parsed.success || busy) return;
    setBusy(true);
    setServerError(null);
    const err = await onSave(parsed.data);
    if (err) {
      setServerError(err);
      setBusy(false);
    }
    // On success the parent unmounts us; no need to reset busy.
  };

  const error = serverError ?? localError;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose a nickname"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: 'rgba(4,10,6,.82)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        animation: 'nkFade .25s ease-out',
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{
          width: 'min(92vw, 420px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          padding: 'clamp(22px,5vw,34px)',
          borderRadius: 18,
          border: '1px solid rgba(166,255,46,.22)',
          background: 'linear-gradient(160deg, rgba(18,26,16,.96), rgba(10,16,10,.96))',
          boxShadow: '0 24px 70px rgba(0,0,0,.6)',
          animation: 'nkPop .3s cubic-bezier(.2,.9,.3,1.2)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
          <span style={{ fontSize: 30 }}>🏷️</span>
          <span style={{ fontWeight: 800, fontSize: 22, letterSpacing: '.04em' }}>PICK YOUR NICKNAME</span>
          <span style={{ fontSize: 13, color: 'rgba(234,255,234,.55)', lineHeight: 1.5 }}>
            This is what rivals see on the board. You can change it later.
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontSize: 11, letterSpacing: '.2em', fontWeight: 600, color: 'rgba(234,255,234,.45)' }}>NICKNAME</span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '14px 16px',
              border: `1px solid ${error ? 'rgba(255,46,77,.5)' : value.length > 0 ? 'rgba(166,255,46,.4)' : 'rgba(234,255,234,.14)'}`,
              borderRadius: 12,
              background: 'rgba(255,255,255,.03)',
            }}
          >
            <input
              autoFocus
              value={value}
              maxLength={NICKNAME_MAX}
              onChange={(e) => { setValue(e.target.value); setServerError(null); }}
              placeholder="RacerX"
              aria-label="Nickname"
              style={{
                flex: 1,
                minWidth: 0,
                background: 'none',
                border: 'none',
                outline: 'none',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: '.02em',
                color: 'rgba(234,255,234,.95)',
              }}
            />
            <span style={{ fontSize: 12, color: 'rgba(234,255,234,.35)', fontFamily: "'JetBrains Mono',monospace" }}>
              {value.length}/{NICKNAME_MAX}
            </span>
          </div>
          <div style={{ minHeight: 16, fontSize: 12, color: error ? color.redBright : 'rgba(234,255,234,.4)' }}>
            {error ?? ' '}
          </div>
        </div>

        <PrimaryButton disabled={!canSave} style={{ padding: 16, fontSize: 16 }}>
          {busy ? 'SAVING…' : 'ENTER THE ARENA'}
        </PrimaryButton>
      </form>
    </div>
  );
}
